import { Box, Flex, Text } from '@chakra-ui/layout';
import { Icon, IconButton, Spinner } from '@chakra-ui/react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { IconArrowRotateLeftRight } from 'central-icons/IconArrowRotateLeftRight';
import { formatDistanceToNow, intlFormat } from 'date-fns';
import {
  isValidElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrevious } from 'react-use';

import { isRoleRevelioAdmin, useGetLoggedInUser } from '@revelio/auth';
import { DeliveryStatus } from '@revelio/data-access';
import { TableChart } from '@revelio/replots';

import { useEffectiveUserId } from '../../adminRewrite/impersonation/useEffectiveUserId';
import type { ReportRun } from '../../CustomDash/components/Reports/types';
import { useReportRunsQuery } from '../../CustomDash/hooks/useReportRunQuery';
import {
  type UserOption,
  UserSelector,
} from '../../DataBuilder/components/user-selector';
import { useAllReports } from '../hooks/use-all-reports';
import { ReportsStatusColumn } from './reports-status-column';
import styles from './reports-table.module.css';
import { useReportTableData } from './use-report-table-data';

type ReportRow = ReturnType<typeof useReportTableData>['data'][number];

const DEFAULT_SORTING: SortingState = [{ id: 'created_at', desc: true }];

export const ReportsTable = ({
  onInitialized,
}: {
  onInitialized?: () => void;
}) => {
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [timeDistance, setTimeDistance] = useState<string>('');
  const minsPast = useRef<number>(0);
  const [selectedUserOption, setSelectedUserOption] = useState<UserOption>();
  const { loggedInUser } = useGetLoggedInUser();
  const effectiveUserId = useEffectiveUserId();
  const { data, fetching, refresh } = useReportTableData(selectedUserOption);
  const { reports, executeQuery } = useAllReports({
    userId: selectedUserOption?.value,
  });
  const { data: reportRuns = [] } = useReportRunsQuery(
    selectedUserOption?.value || '',
    !!selectedUserOption?.value
  );
  const previousFetch = usePrevious(fetching);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

  useEffect(() => {
    if (hasInitialized && onInitialized) {
      onInitialized();
    }
  }, [hasInitialized, onInitialized]);

  useEffect(() => {
    if (previousFetch === true && fetching === false) {
      setHasInitialized(true);
    }
  }, [previousFetch, fetching]);

  /** Re-sync the picker when the effective user changes (impersonation applied or
   * lifted); a manual selection persists until then. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: loggedInUser supplies display-only label fields; the picker must re-sync only when the effective user changes
  useEffect(() => {
    if (effectiveUserId) {
      setSelectedUserOption({
        value: effectiveUserId,
        label: loggedInUser.name || '',
        group: loggedInUser.client_name || '',
        secondaryLabel: loggedInUser.email || '',
      });
    }
  }, [effectiveUserId]);

  const showUserSelector = isRoleRevelioAdmin(loggedInUser.role);

  const refreshNow = useCallback(() => {
    setLastUpdate(new Date());
    refresh();
    minsPast.current = 0;
  }, [refresh]);

  const intChecker = useCallback(() => {
    minsPast.current++;
    if (minsPast.current >= 4) {
      refreshNow();
    }
  }, [refreshNow]);

  useEffect(() => {
    setTimeDistance(formatDistanceToNow(lastUpdate, { addSuffix: true }));

    const intRefresh = setInterval(() => {
      setTimeDistance(formatDistanceToNow(lastUpdate, { addSuffix: true }));
      intChecker();
    }, 60000);

    return () => clearInterval(intRefresh);
  }, [intChecker, lastUpdate]);

  const secondaryColumn: ColumnDef<ReportRow, unknown> =
    !selectedUserOption?.isGroupOption
      ? { accessorKey: 'client', header: 'Client' }
      : { accessorKey: 'user', header: 'User' };

  const columns = useMemo<ColumnDef<ReportRow, unknown>[]>(
    () => [
      { accessorKey: 'name', header: 'Name' },
      {
        accessorKey: 'reportType',
        header: 'Report Type',
        cell: (info) => <div>{String(info.getValue() ?? '')}</div>,
      },
      secondaryColumn,
      {
        accessorKey: 'created_at',
        header: 'Creation',
        cell: (info) => (
          <div>
            {intlFormat(new Date(String(info.getValue())), {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              timeZoneName: 'short',
            })}
          </div>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: (info) => (
          <div>
            {formatDistanceToNow(new Date(String(info.getValue())), {
              addSuffix: true,
            })}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ getValue, row }) => {
          const tableData = row.original;
          return (
            <ReportsStatusColumn
              viewReportLink={tableData.viewReportLink}
              value={String(getValue())}
              report={reports.find((r) => r && r.id === tableData.id) || null}
              customReportRun={
                reportRuns.find((r: ReportRun) => r && r.id === tableData.id) ||
                null
              }
              reportStatus={tableData.status ?? DeliveryStatus.Undefined}
              refreshReports={executeQuery}
            />
          );
        },
      },
    ],
    [secondaryColumn, reports, reportRuns, executeQuery]
  );

  // Make rows navigable (Finished rows only), mirroring the screener's pattern.
  const onRenderRow = useCallback(
    ({
      rowContent,
      rowData,
    }: {
      rowContent: ReactNode;
      rowData: ReportRow;
    }) => {
      if (!isValidElement(rowContent)) {
        return rowContent;
      }
      const clickable = rowData.status === DeliveryStatus.Finished;
      const link = rowData.viewReportLink;
      const originalClassName = rowContent.props?.className || '';
      const originalStyle = rowContent.props?.style || {};
      return (
        <tr
          className={originalClassName}
          style={{
            ...originalStyle,
            cursor: clickable ? 'pointer' : 'default',
          }}
          onClick={() => {
            if (clickable && link) navigate(link);
          }}
        >
          {rowContent.props?.children}
        </tr>
      );
    },
    [navigate]
  );

  const hasData = data.length > 0;

  let tableContent: ReactNode;
  if (!hasData && fetching) {
    tableContent = (
      <Flex h="100%" alignItems="center" justifyContent="center">
        <Spinner className={styles.loadingSpinner} />
      </Flex>
    );
  } else if (hasData) {
    tableContent = (
      <TableChart
        data={data}
        columns={columns}
        showZebraStripe={false}
        onRenderRow={onRenderRow}
        controlledSorting={{
          externalSortingState: sorting,
          onSortingChange: setSorting,
        }}
      />
    );
  } else {
    tableContent = (
      <Flex
        h="100%"
        alignItems="center"
        justifyContent="center"
        className={styles.emptyText}
      >
        <Text>No reports created yet</Text>
      </Flex>
    );
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="end">
        {showUserSelector && (
          <UserSelector
            selectedUserOption={selectedUserOption}
            setSelectedUserOption={setSelectedUserOption}
          />
        )}
      </Box>
      <Box
        className={styles.gridContainer}
        rounded="lg"
        h={`calc(100vh - ${showUserSelector ? '169px' : '100px'})`}
        py={4}
      >
        <Box className={styles.tableWrap} position="relative" h="100%" w="100%">
          {/* Refresh control, top-right over the table (replaces the old column
              menu slot). */}
          <IconButton
            aria-label="Refresh Status"
            icon={<IconArrowRotateLeftRight size={14} />}
            className={styles.refreshButton}
            position="absolute"
            top="8px"
            right="19px"
            zIndex={2}
            borderRadius="2px"
            colorScheme="gray"
            variant="outline"
            size="xs"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              refreshNow();
            }}
          />
          {tableContent}
        </Box>
      </Box>
      {lastUpdate && (
        <Flex alignItems="center" justifyContent="flex-end">
          <Text
            className={styles.lastUpdated}
            fontSize="xs"
            pt={2}
            opacity={0.5}
          >
            <Icon
              as={IconArrowRotateLeftRight}
              mr={1}
              top="1px"
              pos="relative"
            />
            <span>Last updated: {timeDistance}</span>
          </Text>
        </Flex>
      )}
    </>
  );
};
